import React, { useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import SideMenu from '../../components/layout/SideMenu';
import AtendimentoForm from '../../components/AtendimentoForm';
import HistoricoAtendimentos from '../../components/HistoricoAtendimentos';
import ObservacaoModal from '../../components/modals/ObservacaoModal';
import useAtendimentoData from '../../hooks/useAtendimentoData';
import PrescriptionForm from '../../components/forms/PrescriptionForm';
import AtestadoMedicoForm from '../../components/forms/AtestadoMedicoForm';
import PedidoDeCirurgiaForm from '../../components/forms/PedidoDeCirurgiaForm';

function AtendimentoPage() {
  const {
    activePage,
    setActivePage,
    nomePaciente,
    setNomePaciente,
    clinica,
    setClinica,
    dataAtendimento,
    setDataAtendimento,
    observacao,
    setObservacao,
    historicoAtendimentos,
    setHistoricoAtendimentos,
    clinicas,
    pacientes,
    isObservacaoModalOpen,
    setIsObservacaoModalOpen,
    modalObservacao,
    setModalObservacao,
    handleSubmit,
    handleObservacaoModalOpen,
    handleUseObservacao,
    handleSubmitPedidoCirurgia,
  } = useAtendimentoData();

  // Função para autopreencher a observação
  const handleAutoFill = () => {
    const anamnese = `
    <h3>Identificação:</h3>
    Idade: [Idade do Paciente] anos<br>
    Sexo: [Masculino/Feminino]</p><br>
    <h3>Queixa Principal (QP):</h3>
    <p>Paciente relata [incluir breve descrição do sintoma principal].</p><br>
    <h3>História da Moléstia Atual (HMA):</h3>
    <p>Início dos sintomas há [dias/meses]. Paciente refere [detalhes do sintoma: localização, intensidade, evolução]. Tratamento inicial com [medicamentos ou ações], sem melhora significativa.</p><br>
    <h3>Revisão de Sistemas:</h3>
    <p>Negado febre, perda de peso, cansaço, ou outros sintomas sistêmicos relevantes.</p><br>
    <h3>História Patológica Pregressa:</h3>
    <p>Antecedentes de [diabetes, hipertensão, alergias, cirurgias anteriores, etc.]. Em uso de [medicações regulares].</p><br>
    <h3>História Familiar:</h3>
    <p>Histórico familiar de [doenças crônicas, como hipertensão, diabetes, cardiopatias, etc.].</p><br>
    <h3>História Social/Hábitos de Vida:</h3>
    <p>Paciente é [fumante/não fumante], ingere [álcool/sedentário/pratica atividades físicas regularmente].</p>
    `;
    setModalObservacao(anamnese);
  };

  // Função para limpar a observação
  const handleClearObservacao = () => {
    setModalObservacao('');
  };

  // Função para abrir o modal de observação
  const openObservacaoModal = (observacao, isNewPatient) => {
    setModalObservacao(observacao || ''); // Se não houver observação, inicializa vazia
    setIsObservacaoModalOpen(true);
  };

  // Função para pegar a data e hora local corretamente
  const getLocalDateTime = () => {
    const now = new Date();
    const offsetMs = now.getTimezoneOffset() * 60 * 1000; // Pega o offset do fuso horário local
    const localTime = new Date(now.getTime() - offsetMs); // Ajusta a data com base no fuso horário
    return localTime.toISOString().slice(0, 16); // Formata para o input type datetime-local
  };

  // Preenche a data e hora atuais com o horário local do sistema
  useEffect(() => {
    setDataAtendimento(getLocalDateTime());
  }, [setDataAtendimento]);

  return (
    <Layout data-testid="layout">
      <div
        className="flex flex-col lg:flex-row p-4 lg:p-6 gap-6"
        data-testid="main-container"
      >
        {/* Menu lateral */}
        <SideMenu
          onMenuItemClick={setActivePage}
          className="mb-6 lg:mb-0 lg:w-1/4"
          data-testid="side-menu"
        />

        {/* Conteúdo principal */}
        <div
          className="flex flex-col lg:flex-row lg:ml-16 lg:flex-1 gap-1"
          data-testid="content-container"
        >
          <div className="flex-1" data-testid="main-area">
            {activePage === 'atendimento' && (
              <AtendimentoForm
                nomePaciente={nomePaciente}
                setNomePaciente={setNomePaciente}
                clinica={clinica}
                setClinica={setClinica}
                dataAtendimento={dataAtendimento}
                setDataAtendimento={setDataAtendimento}
                observacao={observacao}
                setObservacao={setObservacao}
                clinicas={clinicas}
                pacientes={pacientes}
                onSubmit={handleSubmit}
                setHistoricoAtendimentos={setHistoricoAtendimentos}
                onExpand={() => openObservacaoModal(observacao, false)} // Função para abrir o modal
                data-testid="atendimento-form"
              />
            )}

            {activePage === 'receita' && (
              <PrescriptionForm
                nomePaciente={nomePaciente}
                clinica={clinica}
                dataAtendimento={dataAtendimento}
                observacao={observacao}
                data-testid="prescription-form"
              />
            )}

            {activePage === 'atestado' && (
              <AtestadoMedicoForm
                nomePaciente={nomePaciente}
                clinica={clinica}
                dataAtendimento={dataAtendimento}
                onClose={() => setActivePage('atendimento')}
                data-testid="atestado-form"
              />
            )}

            {activePage === 'pedidosCirurgia' && (
              <PedidoDeCirurgiaForm
                nomePaciente={nomePaciente}
                clinica={clinica}
                onSubmit={handleSubmitPedidoCirurgia}
                data-testid="pedido-cirurgia-form"
              />
            )}
          </div>

          {/* Histórico de Atendimentos */}
          <div className="lg:w-1/3 lg:ml-8" data-testid="historico-container">
            <HistoricoAtendimentos
              historicoAtendimentos={historicoAtendimentos}
              onViewObservacao={handleObservacaoModalOpen}
              data-testid="historico-atendimentos"
            />
          </div>
        </div>
      </div>

      {/* Modal de Observações */}
      <ObservacaoModal
        isOpen={isObservacaoModalOpen}
        onClose={() => setIsObservacaoModalOpen(false)}
        value={modalObservacao}
        onChange={setModalObservacao}
        onSave={handleUseObservacao}
        handleAutoFill={handleAutoFill} // Passando a função handleAutoFill
        handleClearObservacao={handleClearObservacao} // Passando a função handleClearObservacao
        isNewPatient={false}
        atendimentoDataHora={dataAtendimento}
        data-testid="observacao-modal"
        callerComponent="PatientEditModal"
      />
    </Layout>
  );
}

export default AtendimentoPage;
